import React, { useState, useEffect } from "react";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export interface CityControlProps {
  /** Starting city value */
  initialValue?: string;
  /** Called with new valid value */
  onChange: (value: string) => void;
}

export const CITY_OPTIONS = [
  "Blanchard", "Cashion", "Tuttle", "Nichols Hills", "Jones", "Coweta", "Guthrie",
  "Skiatook", "Mustang", "Mounds", "Sapulpa", "Goldsby", "Glenpool", "Collinsville",
  "Edmond", "Owasso", "Bixby", "Piedmont", "El Reno", "Luther", "Deer Creek", "Sperry",
  "Stillwater", "Oklahoma City", "Arcadia", "Newcastle", "Pryor", "Sand Springs",
  "Midwest City", "Jenks", "The Village", "Tulsa", "Choctaw", "Moore", "Catoosa",
  "Oologah", "Broken Arrow", "Owner's Land", "Spencer", "Claremore", "Norman", "Yukon", "Bethany"
].sort();

/**
 * Themed select dropdown for City selection.
 * Automatically adapts to system light/dark mode.
 */
export const CityControl: React.FC<CityControlProps> = ({
  initialValue = "Tulsa",
  onChange,
}) => {
  const theme = useSystemTheme();

  // Retrieve colors from current theme
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const cardBg = css.getPropertyValue("--card-bg").trim();
  const inputBg =
    css.getPropertyValue("--input-bg").trim() || cardBg || "var(--card-bg)";
  const labelColor = css.getPropertyValue("--muted-text-color").trim() || textColor;
  const hoverBg =
    css.getPropertyValue("--hover-bg").trim() || "rgba(0,0,0,0.05)";

  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (!CITY_OPTIONS.includes(value)) {
      setError("Invalid city selected.");
    } else {
      setError("");
      onChange(value);
    }
  }, [value, onChange]);

  const handleMouseOver = (e: React.MouseEvent<HTMLSelectElement>) => {
    e.currentTarget.style.backgroundColor = hoverBg;
    e.currentTarget.style.borderColor = borderColor;
  };

  const handleMouseOut = (e: React.MouseEvent<HTMLSelectElement>) => {
    e.currentTarget.style.backgroundColor = inputBg;
    e.currentTarget.style.borderColor = borderColor;
  };

  const selectClass =
    "w-full h-10 text-sm text-center rounded shadow-sm focus:outline-none cursor-pointer transition-all duration-150";
  const selectStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <Label htmlFor="citySelect" className={labelClass} style={labelStyle}>
        City
      </Label>

      <select
        id="citySelect"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className={selectClass}
        style={selectStyle}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        {CITY_OPTIONS.map((city) => (
          <option key={city} value={city} style={{ background: cardBg }}>
            {city}
          </option>
        ))}
      </select>

      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};